// Locale-aware wrappers over the vendored design-system components (#890).
//
// The bundle is shared and knows nothing about the Cockpit's language switch, so its built-in
// accessible names are German literals. An operator working in English used to hear "Schließen"
// and "Brotkrumen" — names that are invisible on screen, so nothing about the interface revealed
// that they had not switched with everything else.
//
// These wrappers supply those names from the operator's dictionary. Each is a default: a caller
// that passes its own name still wins, and every other prop passes through untouched.
import React from "react";
import { cmp, type DSComponent, type DSProps } from "./bundle";
import { useT } from "../i18n";

const RawBanner = cmp("Banner");
const RawBreadcrumb = cmp("Breadcrumb");
const RawDataTable = cmp("DataTable");
const RawFormField = cmp("FormField");
const RawSwitch = cmp("Switch");

/** A dismissible alert. `closeLabel` names the close button for assistive technology. */
export function Banner({ closeLabel, ...rest }: DSProps) {
  const t = useT();
  return <RawBanner closeLabel={closeLabel ?? t("common.close")} {...rest} />;
}

/** A location trail. `label` names the navigation landmark. */
export function Breadcrumb({ label, ...rest }: DSProps) {
  const t = useT();
  return <RawBreadcrumb label={label ?? t("ds.breadcrumbLabel")} {...rest} />;
}

/**
 * The cockpit list. Both call sites already translate their own empty and error copy; these are
 * the names the component supplies on its own — the bulk-select checkboxes, which have no visible
 * label at all and would otherwise be German whatever the operator chose.
 */
export function DataTable({ selectAllLabel, selectRowLabel, ...rest }: DSProps) {
  const t = useT();
  return (
    <RawDataTable
      selectAllLabel={selectAllLabel ?? t("ds.table.selectAll")}
      selectRowLabel={selectRowLabel ?? t("ds.table.selectRow")}
      {...rest}
    />
  );
}

/** A labelled form control. `optionalLabel` marks a field an operator may leave blank. */
export function FormField({ optionalLabel, ...rest }: DSProps) {
  const t = useT();
  return <RawFormField optionalLabel={optionalLabel ?? t("ds.field.optional")} {...rest} />;
}

/** An on/off control. `onLabel`/`offLabel` are the visible state next to it (#2370). */
export function Switch({ onLabel, offLabel, ...rest }: DSProps) {
  const t = useT();
  return <RawSwitch onLabel={onLabel ?? t("ds.switch.on")} offLabel={offLabel ?? t("ds.switch.off")} {...rest} />;
}

// Typed as plain components above so the JSX is readable; the facade re-exports them under the
// same names the screens already import.
export type { DSComponent };
